<template>
  <div>
    <v-card class="mb-6">
      <v-card-title class="d-flex align-center">
        <v-icon size="large" class="mr-2">mdi-bell-ring</v-icon>
        <span>Notifier</span>
      </v-card-title>
      <v-card-subtitle>
        Send notifications to teachers and invite new users
      </v-card-subtitle>

      <v-card-text>
        <v-row>
          <!-- Inventory Notification Card -->
          <v-col cols="12" md="6">
            <v-card variant="outlined" class="h-100">
              <v-card-title class="text-h6">
                <v-icon start color="primary">mdi-package-variant</v-icon>
                Inventory Notification
              </v-card-title>
              <v-card-text>
                <p class="text-body-2 mb-3">
                  Send an email notification to all teachers who are assignees of pending requests,
                  informing them about currently available equipment.
                </p>

                <v-alert
                  v-if="inventoryStats"
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="mb-3"
                >
                  <div class="text-body-2">
                    <strong>{{ inventoryStats.availableCount }}</strong> device(s) available
                  </div>
                  <div v-if="inventoryStats.byType && Object.keys(inventoryStats.byType).length > 0" class="text-caption mt-1">
                    <div v-for="(count, type) in inventoryStats.byType" :key="type">
                      {{ count }} {{ type }}{{ count !== 1 ? 's' : '' }}
                    </div>
                  </div>
                  <div class="text-caption mt-2">
                    <strong>{{ inventoryStats.teacherCount }}</strong> teacher(s) with pending requests
                  </div>
                </v-alert>

                <v-alert
                  v-if="inventoryNotificationResult && !inventoryNotificationError"
                  type="success"
                  variant="tonal"
                  density="compact"
                  class="mb-3"
                  closable
                  @click:close="inventoryNotificationResult = null"
                >
                  {{ inventoryNotificationResult }}
                </v-alert>

                <v-alert
                  v-if="inventoryNotificationError"
                  type="error"
                  variant="tonal"
                  density="compact"
                  class="mb-3"
                  closable
                  @click:close="inventoryNotificationError = null"
                >
                  {{ inventoryNotificationError }}
                </v-alert>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  variant="elevated"
                  :loading="sendingInventoryNotification"
                  :disabled="!inventoryStats || inventoryStats.teacherCount === 0"
                  @click="sendInventoryNotification"
                >
                  <v-icon start>mdi-email-send</v-icon>
                  Send Notification
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>

          <!-- Teacher Invitation Card -->
          <v-col cols="12" md="6">
            <v-card variant="outlined" class="h-100">
              <v-card-title class="text-h6">
                <v-icon start color="success">mdi-account-plus</v-icon>
                Invite Teacher
              </v-card-title>
              <v-card-text>
                <p class="text-body-2 mb-3">
                  Send an invitation email to a new teacher, providing them with a link to create an account.
                </p>

                <v-form ref="inviteFormRef" @submit.prevent="inviteTeacher">
                  <v-text-field
                    v-model="inviteForm.teacher_name"
                    label="Teacher Name"
                    variant="outlined"
                    density="compact"
                    :rules="[v => !!v || 'Name is required']"
                    required
                    class="mb-2"
                  ></v-text-field>

                  <v-text-field
                    v-model="inviteForm.teacher_email"
                    label="Teacher Email"
                    type="email"
                    variant="outlined"
                    density="compact"
                    :rules="[
                      v => !!v || 'Email is required',
                      v => /.+@.+\..+/.test(v) || 'Email must be valid'
                    ]"
                    required
                    class="mb-2"
                  ></v-text-field>

                  <v-alert
                    v-if="inviteResult && !inviteError"
                    type="success"
                    variant="tonal"
                    density="compact"
                    class="mb-3"
                    closable
                    @click:close="inviteResult = null"
                  >
                    {{ inviteResult }}
                  </v-alert>

                  <v-alert
                    v-if="inviteError"
                    type="error"
                    variant="tonal"
                    density="compact"
                    class="mb-3"
                    closable
                    @click:close="inviteError = null"
                  >
                    {{ inviteError }}
                  </v-alert>
                </v-form>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="success"
                  variant="elevated"
                  :loading="sendingInvite"
                  @click="inviteTeacher"
                >
                  <v-icon start>mdi-email-send</v-icon>
                  Send Invitation
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { api } from '@/composables/api'

// Inventory notification state
const sendingInventoryNotification = ref(false)
const inventoryNotificationResult = ref<string | null>(null)
const inventoryNotificationError = ref<string | null>(null)
const inventoryStats = ref<any>(null)

// Teacher invitation state
const sendingInvite = ref(false)
const inviteResult = ref<string | null>(null)
const inviteError = ref<string | null>(null)
const inviteFormRef = ref<any>(null)
const inviteForm = ref({
  teacher_name: '',
  teacher_email: ''
})

// Load inventory stats
const loadInventoryStats = async () => {
  try {
    // Get available equipment count
    const equipmentResponse = await api.get('/equipment', { status: 'available' })
    const availableEquipment = equipmentResponse.data || []

    // Count by type
    const byType: Record<string, number> = {}
    availableEquipment.forEach((eq: any) => {
      const type = eq.equipment_type || 'other'
      byType[type] = (byType[type] || 0) + 1
    })

    // Get pending requests to count unique teachers
    const requestsResponse = await api.get('/requests', { status: 'pending' })
    const pendingRequests = requestsResponse.data || []

    // Count unique teacher emails
    const teacherEmails = new Set<string>()
    pendingRequests.forEach((req: any) => {
      if (req.teacher_email) {
        teacherEmails.add(req.teacher_email)
      }
    })

    inventoryStats.value = {
      availableCount: availableEquipment.length,
      byType,
      teacherCount: teacherEmails.size
    }
  } catch (error) {
    console.error('Error loading inventory stats:', error)
  }
}

// Send inventory notification
const sendInventoryNotification = async () => {
  sendingInventoryNotification.value = true
  inventoryNotificationResult.value = null
  inventoryNotificationError.value = null

  try {
    const response = await api.post('/notifications/send-inventory-notification', {})

    if (response.data.success) {
      inventoryNotificationResult.value = response.data.message
    } else {
      inventoryNotificationError.value = response.data.message || 'Failed to send notifications'
    }
  } catch (error: any) {
    console.error('Error sending inventory notification:', error)
    inventoryNotificationError.value = error.response?.data?.detail || 'Failed to send inventory notification. Please try again.'
  } finally {
    sendingInventoryNotification.value = false
  }
}

// Invite teacher
const inviteTeacher = async () => {
  // Validate form
  const { valid } = await inviteFormRef.value.validate()
  if (!valid) return

  sendingInvite.value = true
  inviteResult.value = null
  inviteError.value = null

  try {
    const response = await api.post('/notifications/invite-teacher', {
      teacher_name: inviteForm.value.teacher_name,
      teacher_email: inviteForm.value.teacher_email
    })

    if (response.data.success) {
      inviteResult.value = response.data.message
      // Reset form
      inviteForm.value = {
        teacher_name: '',
        teacher_email: ''
      }
      inviteFormRef.value.resetValidation()
    } else {
      inviteError.value = response.data.message || 'Failed to send invitation'
    }
  } catch (error: any) {
    console.error('Error inviting teacher:', error)
    inviteError.value = error.response?.data?.detail || 'Failed to send invitation. Please try again.'
  } finally {
    sendingInvite.value = false
  }
}

onMounted(() => {
  loadInventoryStats()
})
</script>

<style scoped>
.h-100 {
  height: 100%;
}
</style>
